import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import { ReceiptLongOutlined } from '@mui/icons-material';

import { StudentProfileResponse } from '../../../services/studentProfileService';
import {
  FEE_INVOICE_KIND_LABEL, FEE_INVOICE_STATUS_META, FeeInvoiceKind, FeeInvoiceStatus, FeePaymentMethod,
} from '../../../types/fees';
import { useTimezone } from '../../../hooks/useTimezone';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';
import { SectionCard } from '../../common/SectionCard';
import { ToneChip } from '../../common/StatusChip';
import EmptyState from '../../common/EmptyState';
import InvoiceDetailDialog from '../../fees/InvoiceDetailDialog';
import { feeTotals } from './profileFees';
import { PAYMENT_METHOD_LABEL } from '../../../utils/paymentMethods';

/**
 * Every challan for this person — application, admission and tuition alike —
 * each with the payments that settled it underneath. Click a challan for
 * its full breakdown.
 */
const StudentFeesLifetimeTab: React.FC<{ fees: StudentProfileResponse['fees']; onChanged?: () => void }> = ({ fees, onChanged }) => {
  const { formatDate } = useTimezone();
  const money = useCurrencyFormat();
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);

  const totals = useMemo(() => feeTotals(fees), [fees]);
  // Receipts that kept or used advance — the family's advance history.
  const advanceMoves = useMemo(() => fees.payments.filter((p: any) => (p.advanceAmount ?? 0) > 0 || (p.fromAdvance ?? 0) > 0), [fees.payments]);

  // Payments under the challan(s) they were allocated to.
  const paidInto = useMemo(() => {
    const map = new Map<string, Array<{ payment: any; amount: number }>>();
    fees.payments.forEach((p: any) => (p.allocations || []).forEach((a: any) => {
      const key = String(a.invoiceId);
      map.set(key, [...(map.get(key) || []), { payment: p, amount: a.amount }]);
    }));
    return map;
  }, [fees.payments]);

  // Money given back, under the challan it was refunded from.
  const refundedFrom = useMemo(() => {
    const map = new Map<string, StudentProfileResponse['fees']['refunds']>();
    fees.refunds.forEach(r => map.set(String(r.invoiceId), [...(map.get(String(r.invoiceId)) || []), r]));
    return map;
  }, [fees.refunds]);

  if (fees.invoices.length === 0 && fees.concessions.length === 0) {
    return (
      <EmptyState
        icon={<ReceiptLongOutlined />}
        title="No fees yet"
        description="Challans and payments appear here once they're issued."
        dense
      />
    );
  }

  return (
    <Stack spacing={2.5}>
      <SectionCard>
        <Stack direction={{ xs: 'column', sm: 'row' }} divider={<Divider orientation="vertical" flexItem />} spacing={{ xs: 1.5, sm: 3 }}>
          <Metric label="Billed" value={money(totals.billed)} />
          <Metric label="Paid" value={money(totals.paid)} color="success.main" />
          {totals.refunded > 0 && <Metric label="Refunded" value={`−${money(totals.refunded)}`} color="text.secondary" />}
          <Metric label="Outstanding" value={totals.outstanding > 0 ? money(totals.outstanding) : 'Clear'} color={totals.outstanding > 0 ? 'warning.main' : 'success.main'} />
          {(totals.advance > 0 || advanceMoves.length > 0) && <Metric label="Advance held" value={money(totals.advance)} color="info.main" />}
        </Stack>
      </SectionCard>

      {advanceMoves.length > 0 && (
        <SectionCard title="Advance">
          <Stack divider={<Divider />} sx={{ mt: -1.5, mb: -1 }}>
            {advanceMoves.map((p: any) => (
              <Stack key={p._id} direction="row" alignItems="center" spacing={2} sx={{ py: 1.25 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {p.advanceAmount > 0 ? 'Kept as advance' : 'Paid from advance'}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' · '}{formatDate(p.receivedAt)}{' · '}<Box component="span" sx={{ fontFamily: 'monospace' }}>{p.receiptNumber}</Box>
                  </Typography>
                </Typography>
                <Typography variant="body2" fontWeight={700} color={p.advanceAmount > 0 ? 'info.main' : 'text.secondary'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {p.advanceAmount > 0 ? `+${money(p.advanceAmount)}` : `−${money(p.fromAdvance)}`}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}

      {fees.invoices.length > 0 && (
        <SectionCard title="Challans">
          <Stack divider={<Divider />} sx={{ mt: -1.5, mb: -1 }}>
            {fees.invoices.map((inv: any) => {
              const status = FEE_INVOICE_STATUS_META[inv.status as FeeInvoiceStatus];
              const payments = paidInto.get(String(inv._id)) || [];
              return (
                <ButtonBase
                  key={inv._id}
                  onClick={() => setOpenInvoiceId(inv._id)}
                  sx={{ display: 'block', textAlign: 'left', borderRadius: 1.5, px: 1, mx: -1, py: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {FEE_INVOICE_KIND_LABEL[inv.kind as FeeInvoiceKind] || 'Challan'}
                        {inv.period?.label && (
                          <Typography component="span" variant="body2" color="text.secondary"> · {inv.period.label}</Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <Box component="span" sx={{ fontFamily: 'monospace' }}>{inv.invoiceNumber}</Box>
                        {inv.dueDate && ` · Due ${formatDate(inv.dueDate)}`}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{money(inv.totals?.net ?? 0)}</Typography>
                    {status && <ToneChip label={status.label} tone={status.tone} />}
                  </Stack>
                  {payments.map(({ payment, amount }) => (
                    <Typography key={payment._id} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 1.5, borderLeft: 2, borderColor: 'success.light' }}>
                      Paid {money(amount)} on {formatDate(payment.receivedAt)}
                      {' · '}{PAYMENT_METHOD_LABEL[payment.method as FeePaymentMethod] || payment.method}
                      {' · '}<Box component="span" sx={{ fontFamily: 'monospace' }}>{payment.receiptNumber}</Box>
                    </Typography>
                  ))}
                  {(refundedFrom.get(String(inv._id)) || []).map(r => (
                    <Typography key={r._id} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 1.5, borderLeft: 2, borderColor: 'warning.light' }}>
                      {r.kind === 'DEPOSIT_REFUND' ? 'Deposit returned' : 'Refunded'} {money(r.amount)} on {formatDate(r.decidedAt)}
                      {r.method && <>{' · '}{PAYMENT_METHOD_LABEL[r.method as FeePaymentMethod] || r.method}</>}
                      {r.reference && <>{' · '}<Box component="span" sx={{ fontFamily: 'monospace' }}>{r.reference}</Box></>}
                    </Typography>
                  ))}
                </ButtonBase>
              );
            })}
          </Stack>
        </SectionCard>
      )}

      {fees.concessions.length > 0 && (
        <SectionCard title="Concessions">
          <Stack divider={<Divider />} sx={{ mt: -1.5, mb: -1 }}>
            {fees.concessions.map((c: any) => (
              <Stack key={c._id} direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{c.label || 'Concession'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      (c.appliesToKinds || []).map((k: FeeInvoiceKind) => FEE_INVOICE_KIND_LABEL[k] || k).join(', '),
                      c.validFrom && `From ${formatDate(c.validFrom)}`,
                      c.validTo && `to ${formatDate(c.validTo)}`,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700}>{c.mode === 'percent' ? `${c.amount}%` : money(c.amount ?? 0)}</Typography>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}

      <InvoiceDetailDialog open={!!openInvoiceId} invoiceId={openInvoiceId} onClose={() => setOpenInvoiceId(null)} onChanged={onChanged} />
    </Stack>
  );
};

const Metric: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color = 'text.primary' }) => (
  <Box sx={{ flex: 1 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color}>{value}</Typography>
  </Box>
);

export default StudentFeesLifetimeTab;
